<template>
  <div class="form-integration-settings-modal">
    <div class="modal-content">
      <p>Configureer hoe deze integratie werkt voor dit formulier.</p>
      <div v-if="loading" class="loading-state">
        <LoadingSpinner />
      </div>

      <div v-else-if="!hasFormSettings" class="no-settings-state">
        <BaseIcon name="info" />
        <h3>Geen formulierinstellingen</h3>
        <p>Deze integratie heeft geen extra instellingen per formulier.</p>
      </div>

      <div v-else class="settings-form">
        <!-- Dynamic form based on integration type -->
        <template v-for="(fieldSchema, fieldKey) in formSettingsSchema" :key="fieldKey">
          <!-- String fields -->
          <div v-if="fieldSchema.type === 'string'" class="form-group">
            <label :for="`field_${fieldKey}`">
              {{ fieldSchema.label }}
              <span v-if="fieldSchema.required" class="required">*</span>
            </label>
            <input
              :id="`field_${fieldKey}`"
              v-model="formSettings[fieldKey]"
              type="text"
              :placeholder="fieldSchema.label"
              :required="fieldSchema.required"
            />
            <small v-if="fieldSchema.description">{{ fieldSchema.description }}</small>
          </div>

          <!-- Number fields -->
          <div v-else-if="fieldSchema.type === 'number'" class="form-group">
            <label :for="`field_${fieldKey}`">
              {{ fieldSchema.label }}
              <span v-if="fieldSchema.required" class="required">*</span>
            </label>
            <input
              :id="`field_${fieldKey}`"
              v-model.number="formSettings[fieldKey]"
              type="number"
              :placeholder="fieldSchema.label"
              :required="fieldSchema.required"
            />
            <small v-if="fieldSchema.description">{{ fieldSchema.description }}</small>
          </div>

          <!-- Boolean fields: use ToggleInput for 'enabled', checkbox for others -->
          <div v-else-if="fieldSchema.type === 'boolean' && fieldKey === 'enabled'" class="form-group">
            <ToggleInput
              :id="`field_${fieldKey}`"
              :label="fieldSchema.label"
              v-model="formSettings[fieldKey]"
            />
          </div>
          <div v-else-if="fieldSchema.type === 'boolean'" class="form-group">
            <label class="checkbox-label">
              <input v-model="formSettings[fieldKey]" type="checkbox" />
              {{ fieldSchema.label }}
            </label>
            <small v-if="fieldSchema.description">{{ fieldSchema.description }}</small>
          </div>

          <!-- Object fields (like field mappings) -->
          <div
            v-else-if="fieldSchema.type === 'object' && fieldKey === 'fieldMappings'"
            class="form-group"
          >
            <label>
              {{ fieldSchema.label }}
              <span v-if="fieldSchema.required" class="required">*</span>
            </label>
            <div class="field-mappings">
              <!-- Standard field mappings -->
              <div
                v-for="(mappingSchema, mappingKey) in fieldSchema.properties"
                :key="mappingKey"
                class="mapping-row"
              >
                <label>{{ mappingSchema.label }}</label>
                <select
                  v-model="formSettings.fieldMappings[mappingKey]"
                  :required="mappingSchema.required"
                  v-if="formSettings.fieldMappings"
                >
                  <option value="">Selecteer veld...</option>
                  <option v-for="field in availableFields" :key="field.id" :value="field.id">
                    {{ field.props ? field.props.label : field.label }}
                  </option>
                </select>
                <small v-if="mappingSchema.description" class="field-description">
                  {{ mappingSchema.description }}
                </small>
              </div>

              <!-- Custom field mappings (Brevo only) -->
              <div
                v-if="fieldSchema.allowCustomFields && integration?.type === 'brevo'"
                class="custom-mappings"
              >
                <h4>Custom Field Mappings</h4>
                <p class="custom-mappings-description">
                  Add custom fields to map to Brevo contact properties (e.g., vierkante meters,
                  budget, etc.)
                </p>

                <div
                  v-for="(customMapping, index) in customFieldMappings"
                  :key="index"
                  class="custom-mapping-row"
                >
                  <div class="custom-mapping-inputs">
                    <div class="input-group">
                      <label>Brevo Field Name</label>
                      <input
                        v-model="customMapping.brevoField"
                        type="text"
                        placeholder="e.g., VIERKANTE_METERS"
                        class="brevo-field-input"
                      />
                    </div>
                    <div class="input-group">
                      <label>Field Type</label>
                      <select v-model="customMapping.fieldType" class="field-type-select">
                        <option value="string">Text</option>
                        <option value="number">Number</option>
                        <option value="boolean">Boolean</option>
                        <option value="date">Date</option>
                      </select>
                    </div>
                    <div class="input-group">
                      <label>Form Field</label>
                      <select v-model="customMapping.formField" class="form-field-select">
                        <option value="">Selecteer formulier veld...</option>
                        <option v-for="field in availableFields" :key="field.id" :value="field.id">
                          {{ field.props ? field.props.label : field.label }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <button @click="removeCustomMapping(index)" class="btn-remove" type="button">
                    <BaseIcon name="trash" />
                  </button>
                </div>

                <button @click="addCustomMapping" class="btn-add-custom" type="button">
                  <BaseIcon name="plus" />
                  Add Custom Field Mapping
                </button>
              </div>
            </div>
            <small v-if="fieldSchema.description">{{ fieldSchema.description }}</small>
          </div>

          <!-- Other object fields (like custom attributes); skip deal for ActiveCampaign -->
          <div
            v-else-if="
              fieldSchema.type === 'object' &&
              !(integration?.type === 'activecampaign' && fieldKey === 'deal')
            "
            class="form-group"
          >
            <label>{{ fieldSchema.label }}</label>
            <div class="key-value-editor">
              <div
                v-for="(value, key) in formSettings[fieldKey] || {}"
                :key="key"
                class="key-value-row"
              >
                <input
                  v-model="formSettings[fieldKey][key]"
                  type="text"
                  :placeholder="`Waarde voor ${key}`"
                />
                <button @click="removeKeyValue(fieldKey, key)" class="btn-remove" type="button">
                  <BaseIcon name="trash" />
                </button>
              </div>
              <div class="add-key-value" v-if="newKeyValue[fieldKey]">
                <input v-model="newKeyValue[fieldKey].key" type="text" placeholder="Key" />
                <input v-model="newKeyValue[fieldKey].value" type="text" placeholder="Value" />
                <button @click="addKeyValue(fieldKey)" class="btn-add" type="button">
                  <BaseIcon name="plus" />
                </button>
              </div>
            </div>
            <small v-if="fieldSchema.description">{{ fieldSchema.description }}</small>
          </div>

          <!-- Array fields; skip ActiveCampaign customFieldMappings and dealFieldMappings -->
          <div
            v-else-if="
              fieldSchema.type === 'array' &&
              !(integration?.type === 'activecampaign' &&
                ['customFieldMappings', 'dealFieldMappings'].includes(fieldKey))
            "
            class="form-group"
          >
            <label>{{ fieldSchema.label }}</label>
            <div class="array-editor">
              <div
                v-for="(item, index) in formSettings[fieldKey] || []"
                :key="index"
                class="array-item"
              >
                <input v-model="formSettings[fieldKey][index]" type="text" placeholder="Waarde" />
                <button @click="removeArrayItem(fieldKey, index)" class="btn-remove" type="button">
                  <BaseIcon name="trash" />
                </button>
              </div>
              <div class="add-array-item" v-if="newArrayValue[fieldKey] !== undefined">
                <input
                  v-model="newArrayValue[fieldKey]"
                  type="text"
                  placeholder="Nieuwe waarde"
                  @keyup.enter="addArrayItem(fieldKey)"
                />
                <button @click="addArrayItem(fieldKey)" class="btn-add" type="button">
                  <BaseIcon name="plus" />
                </button>
              </div>
            </div>
            <small v-if="fieldSchema.description">{{ fieldSchema.description }}</small>
          </div>
        </template>

        <!-- ActiveCampaign-specific: customFieldMappings, deal, dealFieldMappings -->
        <template v-if="integration?.type === 'activecampaign'">
          <div class="form-group activecampaign-section">
            <h4>Custom contact field mappings</h4>
            <p class="field-description">
              Map form fields to ActiveCampaign contact custom fields (use AC field ID from
              metadata).
            </p>
            <div
              v-for="(item, index) in formSettings.customFieldMappings || []"
              :key="'ac-cf-' + index"
              class="custom-mapping-row"
            >
              <div class="custom-mapping-inputs">
                <div class="input-group">
                  <label>Form field</label>
                  <select
                    v-model="item.formField"
                    class="form-field-select"
                  >
                    <option value="">Select form field...</option>
                    <option v-for="field in availableFields" :key="field.id" :value="field.id">
                      {{ field.props ? field.props.label : field.label }}
                    </option>
                  </select>
                </div>
                <div class="input-group">
                  <label>AC contact field ID</label>
                  <input v-model="item.acFieldId" type="text" placeholder="e.g. 1" />
                </div>
              </div>
              <button
                type="button"
                class="btn-remove"
                @click="removeActiveCampaignCustomFieldMapping(index)"
              >
                <BaseIcon name="trash" />
              </button>
            </div>
            <button type="button" class="btn-add-custom" @click="addActiveCampaignCustomFieldMapping">
              <BaseIcon name="plus" />
              Add custom field mapping
            </button>
          </div>

          <div class="form-group activecampaign-section">
            <h4>Deal (create on submission)</h4>
            <label class="checkbox-label">
              <input v-model="formSettings.deal.enabled" type="checkbox" />
              Create a deal on form submission
            </label>
            <template v-if="formSettings.deal?.enabled">
              <div class="mapping-row">
                <label>Deal title</label>
                <input
                  v-model="formSettings.deal.title"
                  type="text"
                  placeholder="e.g. Offerte {{product}}"
                />
              </div>
              <div class="mapping-row">
                <label>Pipeline ID *</label>
                <input v-model="formSettings.deal.pipelineId" type="text" placeholder="AC pipeline ID" />
              </div>
              <div class="mapping-row">
                <label>Stage ID</label>
                <input v-model="formSettings.deal.stageId" type="text" placeholder="AC stage ID (optional)" />
              </div>
              <div class="mapping-row">
                <label>Form field for deal value</label>
                <select v-model="formSettings.deal.valueField" class="form-field-select">
                  <option value="">None</option>
                  <option v-for="field in availableFields" :key="field.id" :value="field.id">
                    {{ field.props ? field.props.label : field.label }}
                  </option>
                </select>
              </div>
              <div class="mapping-row">
                <label>Currency</label>
                <input v-model="formSettings.deal.currency" type="text" placeholder="eur" />
              </div>
            </template>
          </div>

          <div class="form-group activecampaign-section">
            <h4>Deal custom field mappings</h4>
            <p class="field-description">Map form fields to ActiveCampaign deal custom fields.</p>
            <div
              v-for="(item, index) in formSettings.dealFieldMappings || []"
              :key="'ac-df-' + index"
              class="custom-mapping-row"
            >
              <div class="custom-mapping-inputs">
                <div class="input-group">
                  <label>Form field</label>
                  <select v-model="item.formField" class="form-field-select">
                    <option value="">Select form field...</option>
                    <option v-for="field in availableFields" :key="field.id" :value="field.id">
                      {{ field.props ? field.props.label : field.label }}
                    </option>
                  </select>
                </div>
                <div class="input-group">
                  <label>AC deal field ID</label>
                  <input v-model="item.acDealFieldId" type="text" placeholder="e.g. 1" />
                </div>
              </div>
              <button
                type="button"
                class="btn-remove"
                @click="removeActiveCampaignDealFieldMapping(index)"
              >
                <BaseIcon name="trash" />
              </button>
            </div>
            <button type="button" class="btn-add-custom" @click="addActiveCampaignDealFieldMapping">
              <BaseIcon name="plus" />
              Add deal field mapping
            </button>
          </div>
        </template>
      </div>

      <!-- Validation errors -->
      <div v-if="validationErrors.length > 0" class="validation-errors">
        <h4>Validatie Fouten:</h4>
        <ul>
          <li v-for="error in validationErrors" :key="error">{{ error }}</li>
        </ul>
      </div>
    </div>

    <div class="modal-actions">
      <Btn text="Annuleren" variant="outlined" @click="$emit('close')" />
      <Btn
        text="Resetten naar standaard"
        variant="secondary"
        @click="resetToDefaults"
        :disabled="!hasFormSettings"
      />
      <Btn
        text="Instellingen opslaan"
        variant="primary"
        @click="saveSettings"
        :loading="saving"
        :disabled="!isValid || saving"
      />
    </div>
  </div>
</template>

<script>
  import { defineComponent, ref, computed, watch, onMounted } from 'vue';
  import { useIntegrationStore } from '../../store/integrationStore';
  import { useFormStore } from '../../store/formStore';
  import { getProductService } from '@/composables/useProductService';
  import LoadingSpinner from '../partials/LoadingSpinner.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import Btn from '../partials/Btn.vue';
  import ToggleInput from '../inputs/ToggleInput.vue';

  export default defineComponent({
    name: 'FormIntegrationSettingsModal',

    components: {
      LoadingSpinner,
      BaseIcon,
      Btn,
      ToggleInput,
    },

    props: {
      formId: {
        type: String,
        required: true,
      },
      integrationId: {
        type: String,
        required: true,
      },
    },

    emits: ['close', 'settings-updated'],

    setup(props, { emit }) {
      const integrationStore = useIntegrationStore();
      const formStore = useFormStore();

      // Reactive data
      const loading = ref(true);
      const saving = ref(false);
      const formSettings = ref({});
      const newKeyValue = ref({});
      const newArrayValue = ref({});
      const customFieldMappings = ref([]);
      const configuratorFields = ref([]);
      // Full form (with pages + settings.cpq) is loaded on mount via
      // ensureFormDetails — the /forms list response is summary-only.
      const formDetails = ref(null);

      // Computed properties
      const integration = computed(() =>
        integrationStore.integrations.find((i) => i.id === props.integrationId)
      );

      const formSettingsSchema = computed(() =>
        integration.value ? integrationStore.getFormSettingsSchema(integration.value.type) : {}
      );

      const hasFormSettings = computed(() =>
        integration.value ? integrationStore.hasFormSettings(integration.value.type) : false
      );

      const availableFields = computed(() => {
        const form = formDetails.value;
        const fromPages = (
          form?.pages?.flatMap(
            (page) =>
              page.elements?.filter(
                (el) => el.type !== 'text' && (el.props?.label ?? el.label)
              ) || []
          ) || []
        );
        if (form?.type === 'configurator' && configuratorFields.value.length > 0) {
          return [...fromPages, ...configuratorFields.value];
        }
        return fromPages;
      });

      const validationErrors = computed(() => {
        if (!integration.value) return [];
        const validation = integrationStore.validateFormSettings(
          integration.value.type,
          formSettings.value
        );
        return validation.errors;
      });

      const isValid = computed(() => validationErrors.value.length === 0);

      // Initialize reactive objects for dynamic fields
      const initializeHelpers = () => {
        Object.keys(formSettingsSchema.value).forEach((fieldKey) => {
          const fieldSchema = formSettingsSchema.value[fieldKey];

          if (fieldSchema.type === 'object') {
            newKeyValue.value[fieldKey] = { key: '', value: '' };
          } else if (fieldSchema.type === 'array') {
            newArrayValue.value[fieldKey] = '';
          }
        });
      };

      // Methods
      // Contact-details page element IDs from useRuntimeConfiguratorGeneration createContactDetailsPage
      const CONFIGURATOR_CONTACT_FIELDS = [
        { id: 'email-input', props: { label: 'E-mailadres' } },
        { id: 'name-input', props: { label: 'Naam (voornaam + achternaam)' } },
        { id: 'phone-input', props: { label: 'Telefoonnummer' } },
        { id: 'address-input', props: { label: 'Adresgegevens' } },
      ];

      const loadConfiguratorFields = async (form) => {
        const elements = [...CONFIGURATOR_CONTACT_FIELDS];

        const selected = form?.settings?.cpq?.selectedProducts;
        if (selected && Array.isArray(selected)) {
          const productIds = selected
            .map((item) => (typeof item === 'string' ? item : item?.productId || item?.id))
            .filter(Boolean);
          if (productIds.length > 1) {
            elements.push({
              id: 'product-selection-element',
              props: { label: 'Product selection' },
            });
          }
          if (productIds.length > 0) {
            try {
              const productService = await getProductService();
              for (const productId of productIds) {
                try {
                  const product = await productService.getProductById(productId);
                  if (product?.options?.length) {
                    product.options.forEach((option) => {
                      elements.push({
                        id: `${product.id}-${option.id}-input`,
                        props: { label: option.label || option.id },
                      });
                    });
                  }
                } catch (e) {
                  console.warn('Could not load product for integration fields:', productId, e);
                }
              }
            } catch (error) {
              console.error('Failed to load configurator fields:', error);
            }
          }
        }
        configuratorFields.value = elements;
      };

      const loadSettings = async () => {
        if (!integration.value) return;

        loading.value = true;
        try {
          // Get current settings with defaults
          const currentSettings = integrationStore.getIntegrationSettingsWithDefaults(
            props.formId,
            props.integrationId
          );

          // Initialize settings with defaults if empty
          const defaultSettings = integrationStore.getDefaultFormSettings(integration.value.type);
          formSettings.value = { ...defaultSettings, ...currentSettings };

          // Ensure fieldMappings exists for Brevo and ActiveCampaign
          if (
            (integration.value.type === 'brevo' || integration.value.type === 'activecampaign') &&
            !formSettings.value.fieldMappings
          ) {
            formSettings.value.fieldMappings = {};
          }

          // Ensure deal and arrays exist for ActiveCampaign
          if (integration.value.type === 'activecampaign') {
            if (!formSettings.value.deal || typeof formSettings.value.deal !== 'object') {
              formSettings.value.deal = {
                enabled: false,
                title: '{{email}} - Form Submission',
                pipelineId: '',
                stageId: '',
                valueField: '',
                currency: 'eur',
              };
            }
            if (!Array.isArray(formSettings.value.customFieldMappings)) {
              formSettings.value.customFieldMappings = [];
            }
            if (!Array.isArray(formSettings.value.dealFieldMappings)) {
              formSettings.value.dealFieldMappings = [];
            }
          }

          // Load custom field mappings from formSettings
          if (integration.value.type === 'brevo' && formSettings.value.customFieldMappings) {
            customFieldMappings.value = [...formSettings.value.customFieldMappings];
          } else {
            customFieldMappings.value = [];
          }

          // Initialize helper objects
          initializeHelpers();

          // Load configurator-generated fields for dropdown (email, first name, etc.)
          const form = formDetails.value;
          if (form?.type === 'configurator') {
            await loadConfiguratorFields(form);
          } else {
            configuratorFields.value = [];
          }
        } catch (error) {
          console.error('Failed to load form integration settings:', error);
        } finally {
          loading.value = false;
        }
      };

      const saveSettings = async () => {
        if (!integration.value || !isValid.value) return;

        saving.value = true;
        try {
          // Merge custom field mappings into formSettings
          if (integration.value.type === 'brevo') {
            formSettings.value.customFieldMappings = [...customFieldMappings.value];
          }

          await integrationStore.updateIntegrationFormSettings(
            props.formId,
            props.integrationId,
            formSettings.value
          );

          emit('settings-updated', formSettings.value);
          emit('close');
        } catch (error) {
          console.error('Failed to save form integration settings:', error);
          // Here you would show a toast or error message
        } finally {
          saving.value = false;
        }
      };

      const resetToDefaults = () => {
        if (!integration.value) return;

        const defaultSettings = integrationStore.getDefaultFormSettings(integration.value.type);
        formSettings.value = { ...defaultSettings };
        customFieldMappings.value = [];
      };

      const addKeyValue = (fieldKey) => {
        if (!newKeyValue.value[fieldKey]) return;

        const { key, value } = newKeyValue.value[fieldKey];
        if (key && value) {
          if (!formSettings.value[fieldKey]) {
            formSettings.value[fieldKey] = {};
          }
          formSettings.value[fieldKey][key] = value;
          newKeyValue.value[fieldKey] = { key: '', value: '' };
        }
      };

      const removeKeyValue = (fieldKey, key) => {
        if (formSettings.value[fieldKey]) {
          delete formSettings.value[fieldKey][key];
        }
      };

      const addArrayItem = (fieldKey) => {
        if (newArrayValue.value[fieldKey] === undefined) return;

        const value = newArrayValue.value[fieldKey];
        if (value) {
          if (!formSettings.value[fieldKey]) {
            formSettings.value[fieldKey] = [];
          }
          formSettings.value[fieldKey].push(value);
          newArrayValue.value[fieldKey] = '';
        }
      };

      const removeArrayItem = (fieldKey, index) => {
        if (formSettings.value[fieldKey]) {
          formSettings.value[fieldKey].splice(index, 1);
        }
      };

      const addCustomMapping = () => {
        customFieldMappings.value.push({
          brevoField: '',
          fieldType: 'string',
          formField: '',
        });
      };

      const removeCustomMapping = (index) => {
        customFieldMappings.value.splice(index, 1);
      };

      const addActiveCampaignCustomFieldMapping = () => {
        if (!formSettings.value.customFieldMappings) {
          formSettings.value.customFieldMappings = [];
        }
        formSettings.value.customFieldMappings.push({ formField: '', acFieldId: '' });
      };

      const removeActiveCampaignCustomFieldMapping = (index) => {
        formSettings.value.customFieldMappings?.splice(index, 1);
      };

      const addActiveCampaignDealFieldMapping = () => {
        if (!formSettings.value.dealFieldMappings) {
          formSettings.value.dealFieldMappings = [];
        }
        formSettings.value.dealFieldMappings.push({ formField: '', acDealFieldId: '' });
      };

      const removeActiveCampaignDealFieldMapping = (index) => {
        formSettings.value.dealFieldMappings?.splice(index, 1);
      };

      // Load data on mount
      onMounted(async () => {
        // We need the full form (pages + settings.cpq) here; the list endpoint
        // is summary-only. ensureFormDetails caches across calls within the
        // session, so reopening the modal for the same form is instant.
        try {
          formDetails.value = await formStore.ensureFormDetails(props.formId);
        } catch (error) {
          console.error('Failed to load form details for integration settings:', error);
          formDetails.value = null;
        }
        // The integrations list no longer ships per-form settings, so populate
        // formIntegrationMappings[formId][integrationId].settings from the
        // dedicated endpoint before loadSettings reads from it.
        try {
          await integrationStore.fetchFormIntegrationSettings(
            props.formId,
            props.integrationId
          );
        } catch (error) {
          console.error('Failed to load per-form integration settings:', error);
        }
        await loadSettings();
      });

      return {
        loading,
        saving,
        formSettings,
        newKeyValue,
        newArrayValue,
        customFieldMappings,
        integration,
        formSettingsSchema,
        hasFormSettings,
        availableFields,
        validationErrors,
        isValid,
        saveSettings,
        resetToDefaults,
        addKeyValue,
        removeKeyValue,
        addArrayItem,
        removeArrayItem,
        addCustomMapping,
        removeCustomMapping,
        addActiveCampaignCustomFieldMapping,
        removeActiveCampaignCustomFieldMapping,
        addActiveCampaignDealFieldMapping,
        removeActiveCampaignDealFieldMapping,
      };
    },
  });
</script>

<style scoped lang="scss">
  .form-integration-settings-modal {
    width: 100%;
    max-width: 600px;
    max-height: 80vh;
    overflow-y: auto;
  }

  .modal-header {
    margin-bottom: 24px;

    h2 {
      margin: 0 0 8px 0;
      color: #1f2937;
      font-size: 1.5rem;
      font-weight: 600;
    }

    p {
      margin: 0;
      color: #6b7280;
      font-size: 0.875rem;
    }
  }

  .modal-content {
    margin-bottom: 24px;
  }

  .loading-state,
  .no-settings-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    text-align: center;

    h3 {
      margin: 16px 0 8px 0;
      color: #1f2937;
      font-size: 1.125rem;
      font-weight: 500;
    }

    p {
      margin: 0;
      color: #6b7280;
      font-size: 0.875rem;
    }
  }

  .settings-form {
    .form-group {
      margin-bottom: 20px;

      label {
        display: block;
        margin-bottom: 6px;
        font-weight: 500;
        color: #374151;
        font-size: 0.875rem;

        .required {
          color: #ef4444;
          margin-left: 2px;
        }
      }

      input,
      select,
      textarea {
        width: 100%;
        padding: 8px 12px;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        font-size: 0.875rem;
        transition:
          border-color 0.15s ease-in-out,
          box-shadow 0.15s ease-in-out;

        &:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        &:required:invalid {
          border-color: #ef4444;
        }
      }

      small {
        display: block;
        margin-top: 4px;
        color: #6b7280;
        font-size: 0.75rem;
        line-height: 1.4;
      }

      .checkbox-label {
        display: flex;
        align-items: center;
        cursor: pointer;

        input[type='checkbox'] {
          width: auto;
          margin-right: 8px;
        }
      }
    }
  }

  .activecampaign-section {
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid #e5e7eb;

    h4 {
      margin-bottom: 0.5rem;
      font-size: 1rem;
    }
  }

  .field-mappings {
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 16px;
    background: #f9fafb;

    .mapping-row {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 12px;

      &:last-child {
        margin-bottom: 0;
      }

      label {
        min-width: 120px;
        margin-bottom: 0;
        font-size: 0.8125rem;
      }

      select {
        flex: 1;
        margin: 0;
      }

      .field-description {
        margin-left: 10px;
        color: #6b7280;
        font-size: 0.75rem;
      }
    }

    .custom-mappings {
      margin-top: 20px;
      padding-top: 16px;
      border-top: 1px solid #e5e7eb;

      h4 {
        margin-bottom: 8px;
        color: #1f2937;
        font-size: 1rem;
        font-weight: 500;
      }

      .custom-mappings-description {
        margin-bottom: 16px;
        color: #6b7280;
        font-size: 0.8125rem;
        line-height: 1.4;
      }

      .custom-mapping-row {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        margin-bottom: 16px;
        padding: 16px;
        background: #f9fafb;
        border: 1px solid #e5e7eb;
        border-radius: 8px;

        &:last-child {
          margin-bottom: 0;
        }

        .custom-mapping-inputs {
          display: flex;
          flex-direction: column;
          gap: 12px;
          flex: 1;
        }

        .input-group {
          display: flex;
          flex-direction: column;
          gap: 4px;

          label {
            font-size: 0.75rem;
            color: #6b7280;
            font-weight: 500;
          }

          .brevo-field-input {
            padding: 8px 12px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 0.875rem;
            background-color: #fff;

            &:focus {
              outline: none;
              border-color: #3b82f6;
              box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
            }
          }

          .field-type-select,
          .form-field-select {
            padding: 8px 12px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 0.875rem;
            background-color: #fff;
            cursor: pointer;

            &:focus {
              outline: none;
              border-color: #3b82f6;
              box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
            }
          }
        }

        .btn-remove {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.15s ease-in-out;
          background: #fef2f2;
          color: #dc2626;
          flex-shrink: 0;

          &:hover {
            background: #fee2e2;
            transform: scale(1.05);
          }
        }
      }

      .btn-add-custom {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 16px;
        background: #f0fdf4;
        color: #16a34a;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        font-size: 0.875rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.15s ease-in-out;

        &:hover {
          background: #dcfce7;
          border-color: #16a34a;
          transform: translateY(-1px);
        }
      }
    }
  }

  .key-value-editor,
  .array-editor {
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 16px;
    background: #f9fafb;

    .key-value-row,
    .array-item {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;

      input {
        flex: 1;
        margin: 0;
      }
    }

    .add-key-value,
    .add-array-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding-top: 12px;
      border-top: 1px solid #d1d5db;

      input {
        flex: 1;
        margin: 0;
      }
    }

    .btn-remove,
    .btn-add {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      transition: background-color 0.15s ease-in-out;

      &.btn-remove {
        background: #fef2f2;
        color: #dc2626;

        &:hover {
          background: #fee2e2;
        }
      }

      &.btn-add {
        background: #f0fdf4;
        color: #16a34a;

        &:hover {
          background: #dcfce7;
        }
      }
    }
  }

  .validation-errors {
    margin-top: 16px;
    padding: 12px 16px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;

    h4 {
      margin: 0 0 8px 0;
      color: #dc2626;
      font-size: 0.875rem;
      font-weight: 600;
    }

    ul {
      margin: 0;
      padding-left: 16px;

      li {
        color: #dc2626;
        font-size: 0.8125rem;
        margin-bottom: 4px;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  .modal-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid var(--slate-200);
  }
</style>
